import {
    Avatar,
    Box,
    Button,
    DarkMode,
    Divider,
    Flex,
    HStack,
    Heading,
    Icon,
    IconButton,
    LightMode,
    Stack,
    Text,
    Tooltip,
    useBreakpointValue,
    useColorMode,
} from '@chakra-ui/react';
import {useAutoLogout} from 'hooks/useAutoLogout';
import {useColorModeValueKey} from 'hooks/useColors';
import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useLocation, useMatches, useNavigate} from 'react-router-dom';
import {useUIStore} from 'stores/uiStore';
import {HiOutlineMenu} from 'react-icons/hi';
import {camelCase, get, last, split, trim} from 'lodash';
import LanguageBtn from 'components/LanguageBtn';
import ColorModeBtn from 'components/ColorModeBtn';
import NotificationBtn from 'components/NotificationBtn';
import ProfileBtn from 'components/ProfileBtn';
import CustomBreadcrumb from 'components/CustomBreadcrumb';
import {resolveNewUi} from 'components/Versioned';
import {AppLogoArea, MobileNavDrawer, NavDrawer} from './NavBar';
import {useAuth} from 'hooks/useAuth';
import {CgArrowsExchangeAlt} from 'react-icons/cg';
import {designSystemColors} from 'theme';
import {motion} from 'framer-motion';
import {useAdminProfile, useProfile} from 'hooks/datahook/auth';
import ShieldExclamationMini from 'components/Icon/ShieldExclamationMini';
import ShieldCheckMini from 'components/Icon/ShieldCheckMini';
import QRCode from 'components/Icon/QRCode';
import {paths} from 'routes/path';
import BrochureDialog from 'components/Dialog/BrochureDialog';
import PlatformCurrencyBtn from 'components/PlatformCurrencyBtn';
import {
    HiArrowRight,
    HiBanknotes,
    HiCurrencyDollar,
    HiOutlineBanknotes,
    HiPencil,
    HiPlus,
    HiQuestionMarkCircle,
    HiUserPlus,
    HiWallet,
} from 'react-icons/hi2';
import {IoDiamondSharp} from 'react-icons/io5';
import {useDepositStore} from 'stores/depositStore';
import ApplyForexAccount from 'page/app/Forex/ApplyForexAccount';
import ApplyIB from 'page/app/ApplyIB';
import {formatDateTimezone} from '../utils/formator';
import {IoHelpOutline} from 'react-icons/io5';
import {IoIosTime} from 'react-icons/io';
import {addSeconds} from 'date-fns';
import {useForexAccountList} from '../hooks/datahook/forex';
import InviteDialog from 'components/Dialog/InviteDialog';
import {Helmet} from 'react-helmet-async';
import SafeLoadingComponent from 'components/SafeLoadingComponent';

const MotionStack = motion(Stack);
const MotionBox = motion(Box);

const NavBarItem = ({useNewShell = false}) => {
    const {t} = useTranslation('app');
    const {
        data: profile,
        kycVerified,
        kycPending,
        kycRequired,
        isCompany,
        isIB,
    } = useProfile();
    // Old shell (existing): Chakra theme color paths. New shell: design tokens.
    // Pre-compute both at the top so the ternary in JSX is just a value pick
    // (Hook rules: useColorModeValueKey / useColorModeValue can't be conditional).
    const _oldSuccess = useColorModeValueKey('success');
    const _oldText = useColorModeValueKey('text');
    const _oldWarning = useColorModeValueKey('warning');
    const _oldGray = useColorModeValueKey('gray');
    const successColor = useNewShell ? 'var(--mint)' : _oldSuccess;
    const textColor = useNewShell ? 'var(--text)' : _oldText;
    const warningColor = useNewShell ? 'var(--coral)' : _oldWarning;
    const grayColor = useNewShell ? 'var(--text-muted)' : _oldGray;
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);

    const StatusIcon = kycVerified ? ShieldCheckMini : ShieldExclamationMini;

    // G3 v2 §4.1: in the new shell, the avatar is the prototype's
    // .crm-avatar block (46px circle, #D53F8C pink, white initial letter
    // from the user's real name). Per §0 iron rule, no Chakra height/width/
    // borderRadius/bg/color/fontSize — all four properties come from CSS.
    if (useNewShell) {
        const fullName = get(profile, 'name', '') || '';
        const initial = (fullName.trim().charAt(0) || '?').toUpperCase();
        return (
            <>
                <div
                    className="crm-avatar"
                    onClick={() => navigate(paths.profile)}
                    style={{cursor: 'pointer'}}
                >
                    {initial}
                </div>
                <div className="crm-profile-name">{fullName}</div>
                <div className="crm-profile-ref">
                    Ref: {get(profile, 'reference')}
                </div>
                <BrochureDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    profile={profile}
                />
            </>
        );
    }

    return (
        <>
            <HStack px={3} alignItems={'flex-start'}>
                <Avatar
                    height={sideBarOpen ? '40px' : '40px'}
                    width={sideBarOpen ? '40px' : '40px'}
                    // borderRadius={'4'}
                    src={get(profile, 'avatar')}
                    name={get(profile, 'name','')}
                    onClick={() => navigate(paths.profile)}
                    cursor={'pointer'}
                />
                {/* {sideBarOpen && ( */}
                <MotionBox
                    spacing={0}
                    lineHeight={1.3}
                    minWidth={'170px'}
                    animate={
                        sideBarOpen
                            ? {
                                  opacity: 1,
                                  display: 'block',
                              }
                            : {
                                  opacity: 0,
                                  height: '40px',
                                  transitionEnd: {
                                      display: 'none',
                                  },
                              }
                    }
                    transition={'linear'}
                    initial={false}
                    tran
                >
                    <Text mt={0} color={textColor} noOfLines={2}>
                        {get(profile, 'name')}
                    </Text>
                    <Text fontSize={'xs'} color={grayColor}>
                        Ref: {get(profile, 'reference')}
                    </Text>
                    <HStack spacing={1} alignItems={'center'} mt={0} ml={-1}>
                        <Tooltip
                            label={t('kycStatusWith', {
                                status: t(get(profile, 'kycStatus')),
                            })}
                            hasArrow
                        >
                            <IconButton
                                p={0}
                                size="xs"
                                variant={'unstyled'}
                                onClick={() =>
                                    kycRequired || kycPending
                                        ? navigate(
                                              isCompany ? paths.kyb : paths.kyc,
                                          )
                                        : navigate(paths.profileKycReview)
                                }
                                icon={
                                    <StatusIcon
                                        boxSize={4}
                                        color={
                                            kycVerified
                                                ? successColor
                                                : warningColor
                                        }
                                    />
                                }
                            />
                        </Tooltip>
                        {isIB && (
                            <Tooltip label={t('brochure')} hasArrow>
                                <IconButton
                                    p={0}
                                    size="xs"
                                    variant={'unstyled'}
                                    onClick={() => setOpen(true)}
                                    icon={
                                        <QRCode color={textColor} boxSize={4} />
                                    }
                                />
                            </Tooltip>
                        )}
                    </HStack>
                </MotionBox>
                {/* )} */}
            </HStack>
            <BrochureDialog
                open={open}
                onClose={() => setOpen(false)}
                profile={profile}
            />
        </>
    );
};

const Zendesk = () => {
    return (
        <Helmet>
            <script
                async
                id="ze-snippet"
                src="https://static.zdassets.com/ekr/snippet.js?key=741a5ab9-7540-4e55-8328-aec45b86b853"
            ></script>
        </Helmet>
    );
};
const NavBarQuickAccess = ({useNewShell = false}) => {
    const {t} = useTranslation('app');
    const {data: profile, isIB, isRegulationOX_AU} = useProfile();
    const {data: forexData} = useForexAccountList({
        enableOnly: true,
        type: 'Live',
    });
    const depositState = useDepositStore((depositState) => depositState);
    const navigate = useNavigate();
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);
    const [show, setShow] = useState(false);
    const [brochureOpen, setBrochureOpen] = useState(false);
    const [showApplyIB, setShowApplyIB] = useState(false);

    // G3 v5 §11.1: accessList lifted above the new-shell early return so
    // the icon-row (added back per user feedback) can reuse the same 6
    // action descriptors. New shell renders badges + crm-icon-row + the
    // three dialogs; old shell is unchanged.
    const accessList = [
        {
            icon: HiCurrencyDollar,
            label: t('deposit'),
            onClick: () => {
                depositState.reset();
                navigate(paths.newDeposit);
            },
        },
        {
            icon: HiBanknotes,
            label: t('withdraw'),
            onClick: () => navigate(paths.creatWithdrawal),
        },
        {
            icon: HiArrowRight,
            label: t('transfer'),
            onClick: () => navigate(paths.creatTransfer),
        },
        ...(isIB?[
            {
                icon: HiUserPlus,
                label: t('inviteFriend'),
                onClick: () =>
                    navigate(paths.pendingInvitations)
                        // : navigate(paths.referees),
            }
        ]:[]),
        {
            icon: HiPlus,
            label: t('applyAliveAccount'),
            onClick: () => setShow(true),
        },
        ...(isIB
            ? [
                  {
                      icon: IoDiamondSharp,
                      label: t('commission'),
                      onClick: () => navigate(paths.commissions),
                  },
              ]
            : []),
        ...(!isIB && forexData?.length && !isRegulationOX_AU
            ? [
                  {
                      icon: IoDiamondSharp,
                      label: t('applyIB'),
                      onClick: () => setShowApplyIB(true),
                  },
              ]
            : []),
    ];
    if (useNewShell) {
        const kycVerified = get(profile, 'kycStatus') === 'verified';
        return (
            <>
                <div className="crm-badges">
                    <Tooltip
                        label={t('kycStatusWith', {
                            status: t(get(profile, 'kycStatus', '')),
                        })}
                        hasArrow
                    >
                        <span
                            className="crm-badge-icon"
                            onClick={() => navigate(paths.profileKycReview)}
                            style={{cursor: 'pointer'}}
                            title="KYC"
                        >
                            {kycVerified ? '✓' : '!'}
                        </span>
                    </Tooltip>
                    {isIB && (
                        <Tooltip label={t('brochure')} hasArrow>
                            <span
                                className="crm-badge-icon"
                                onClick={() => setBrochureOpen(true)}
                                style={{cursor: 'pointer'}}
                                title="Brochure"
                            >
                                ⚙
                            </span>
                        </Tooltip>
                    )}
                </div>
                <div className="crm-icon-row">
                    {accessList.map((item, index) => (
                        <Tooltip label={item.label} key={index} hasArrow>
                            <span
                                className="crm-icon-btn"
                                onClick={item.onClick}
                                style={{cursor: 'pointer'}}
                            >
                                <Icon as={item.icon} boxSize={'3.5'} />
                            </span>
                        </Tooltip>
                    ))}
                </div>
                <ApplyForexAccount isOpen={show} onClose={() => setShow(false)} />
                <InviteDialog
                    open={brochureOpen}
                    onClose={() => setBrochureOpen(false)}
                    profile={profile}
                />
                <ApplyIB
                    isOpen={showApplyIB}
                    onClose={() => setShowApplyIB(false)}
                />
            </>
        );
    }
    return (
        <HStack pl={14}>
            <MotionBox
                spacing={0}
                lineHeight={1.3}
                animate={
                    sideBarOpen
                        ? {
                              opacity: 1,
                              display: 'block',
                          }
                        : {
                              opacity: 0,
                              height: '0px',
                              transitionEnd: {
                                  display: 'none',
                              },
                          }
                }
                transition={'linear'}
                initial={false}
                tran
            >
                <HStack spacing={1}>
                    {accessList.map((item, index) => (
                        <Tooltip label={item.label} key={index}>
                            <IconButton
                                variant={'outline'}
                                size="xs"
                                icon={<Icon as={item.icon} boxSize={'4'} />}
                                onClick={item.onClick}
                            />
                        </Tooltip>
                    ))}
                </HStack>
            </MotionBox>
            <ApplyForexAccount isOpen={show} onClose={() => setShow(false)} />
            <InviteDialog
                open={brochureOpen}
                onClose={() => setBrochureOpen(false)}
                profile={profile}
            />
            <ApplyIB
                isOpen={showApplyIB}
                onClose={() => setShowApplyIB(false)}
            />
        </HStack>
    );
};

const TimeView = (props) => {
    const {t} = useTranslation('app');
    const [serverDate, setServerDate] = useState(null);
    const { serverDateTime } = useProfile();
    const timerRef = React.useRef(null);

    useEffect(() => {
        if (serverDateTime && (!serverDate || serverDateTime.getTime() !== serverDate.getTime())) {
            setServerDate(serverDateTime);
        }
    }, [serverDateTime]);

    useEffect(() => {
        if (serverDate) {
            timerRef.current = setInterval(() => {
                setServerDate((prevDate) => addSeconds(prevDate, 1));
            }, 1000);
        }
        return () => {
            timerRef.current && clearInterval(timerRef.current);
        };
    }, [serverDate]);

    return (
        <HStack spacing={1} alignItems={'center'}>
            <Box
                    display={'flex'}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    <Icon as={IoIosTime} boxSize={4} />
                </Box>
            <Text fontSize={'xs'}>{`${serverDate
                ? formatDateTimezone(serverDate, Intl.DateTimeFormat().resolvedOptions().timeZone)
                : ''
                }`}</Text>
                
        </HStack>
    );
};

const Header = ({useNewShell = false}) => {
    const {t} = useTranslation('app');
    const {isUser} = useAuth();
    const [headerTitle, headerDesc, showMobileDrawer, setShowMobileDrawer] =
        useUIStore((state) => [
            state.headerTitle,
            state.headerDesc,
            state.showMobileDrawer,
            state.setShowMobileDrawer,
        ]);
    const isMobile = useBreakpointValue({
        base: true,
        md: false,
    });

    const grayColor = useColorModeValueKey('gray');
    const navBg = useColorModeValueKey('navBg');
    const matches = useMatches();
    const matchPath = get(last(matches), 'pathname');
    const mainPath = last(split(trim(matchPath, '/'), '/'));
    const defaultHeaderTitle = t(camelCase(mainPath));

    return (
        <HStack
            alignItems={'center'}
            justifyContent={'space-between'}
            px={{base: 2, md: 4}}
            py={2}
            bg={navBg}
            transition={'background-color 200ms linear'}
            zIndex={2}
            shadow={'sm'}
        >
            <HStack alignItems={'center'} position="relative" flex={1}>
                {isMobile ? (
                    <HStack>
                        <IconButton
                            onClick={() => setShowMobileDrawer(true)}
                            icon={<HiOutlineMenu size={'24px'} />}
                            variant={'ghost'}
                        />
                        <Heading
                            fontWeight={'600'}
                            fontSize={'lg'}
                            textAlign="center"
                            flex={1}
                        >
                            {headerTitle ? headerTitle : defaultHeaderTitle}
                        </Heading>
                    </HStack>
                ) : (
                    <HStack spacing={2} alignItems={'flex-end'}>
                        <Heading fontWeight={'600'} fontSize={'lg'}>
                            {headerTitle ? headerTitle : defaultHeaderTitle}
                        </Heading>
                        {
                            headerDesc ? (
                                <Text color={grayColor} fontSize="xs">
                                    {headerDesc}
                                </Text>
                            ) : null
                            // <CustomBreadcrumb />
                        }
                    </HStack>
                )}
            </HStack>
            <HStack alignItems={'center'} spacing={2}>
                <SafeLoadingComponent>
                    {!isMobile && <TimeView />}
                </SafeLoadingComponent>
                <NotificationBtn />
                {isUser && <PlatformCurrencyBtn />}
                {!isMobile && (
                    <HStack alignItems={'center'} spacing={2}>
                        <LanguageBtn />
                        {/* G3 v3 §9.3: new design tokens are light-only this
                         * batch, and forcing light (see effect below) means
                         * the toggle is a no-op. Keep it hidden in the new
                         * shell so the topbar doesn't show a dead button. */}
                        {!useNewShell && <ColorModeBtn />}
                    </HStack>
                )}
                {!isMobile && (
                    <Divider h={'36px'} orientation="vertical" pl={2} />
                )}
                <ProfileBtn />
            </HStack>
        </HStack>
    );
};

const NavBar = ({useNewShell = false}) => {
    const [sideBarOpen, setSideBarOpen] = useUIStore((state) => [
        state.sideBarOpen,
        state.setSideBarOpen,
    ]);
    const {isUser} = useAuth();
    const {kycVerified} = (isUser ? useProfile : useAdminProfile)();
    const _oldSideBarBg = useColorModeValueKey('sideBarBg');
    const sideBarBg = useNewShell ? 'var(--panel)' : _oldSideBarBg;
    return (
        <MotionStack
            // G3 v2 §4.1: in the new shell the inner content is wrapped in a
            // `<div className="nx">` so the design-system selectors
            // (`.nx .crm-sidebar`, `.nx .crm-logo`, etc.) fire. The MotionStack
            // itself stays classless — `.crm-sidebar` would set its own
            // `display:flex; flex-direction:column; gap:2px; padding:20px 12px`
            // and fight the width-animation. The className is moved to the
            // inner div below.
            animate={{
                width: sideBarOpen ? (useNewShell ? '210px' : '240px') : '64px',
                minWidth: sideBarOpen ? (useNewShell ? '210px' : '240px') : '64px',
            }}
            initial={false}
            zIndex={3}
            shadow={useNewShell ? undefined : 'sideBar'}
            position={'relative'}
            transition={'width 200ms linear, min-width 200ms linear'}
        >
            {useNewShell ? (
                // v2.1 §8.1: the .nx wrapper is a flex child of the column
                // MotionStack. Without these flex props on the wrapper AND
                // on the inner .crm-sidebar, the sidebar stays content-height
                // and the .nx { background:var(--ink) } bleeds through as a
                // gray band from ~y=530 down. The prototype's .crm-shell is
                // a grid (210px 1fr) so the sidebar auto-stretches; we have
                // to recreate that stretch manually in the flex chain.
                <div
                    className="nx"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div className="crm-sidebar" style={{flex: 1}}>
                        <AppLogoArea
                            collapsed={sideBarOpen}
                            useNewShell={useNewShell}
                        />
                        {isUser && (
                            <div className="crm-profile">
                                <NavBarItem useNewShell={useNewShell} />
                                {kycVerified && (
                                    <NavBarQuickAccess useNewShell={useNewShell} />
                                )}
                            </div>
                        )}
                        <NavDrawer useNewShell={useNewShell} />
                    </div>
                </div>
            ) : (
                <Stack
                    bg={sideBarBg}
                    transition={'background-color 200ms linear'}
                    spacing={4}
                    flex={1}
                    pt={1}
                    overflow={'hidden'}
                >
                    <AppLogoArea collapsed={sideBarOpen} useNewShell={useNewShell} />
                    {isUser && (
                        <Stack spacing={1}>
                            <NavBarItem useNewShell={useNewShell} />
                            {kycVerified && <NavBarQuickAccess useNewShell={useNewShell} />}
                        </Stack>
                    )}
                    <NavDrawer useNewShell={useNewShell} />
                </Stack>
            )}
            <IconButton
                position={'absolute'}
                top={0}
                right={-3}
                zIndex={3}
                size="xs"
                shadow={'lg'}
                rounded={'full'}
                icon={
                    <Icon
                        boxSize={'4'}
                        as={CgArrowsExchangeAlt}
                    />
                }
                onClick={() => setSideBarOpen(!sideBarOpen)}
            />
        </MotionStack>
    );
};

const AppLayout = () => {
    useAutoLogout();
    const isMobile = useBreakpointValue({
        base: true,
        md: false,
    });
    const {isUser} = useAuth();
    const { pathname } = useLocation()
    const hasIbAgent = pathname.split('/').includes('ib-agent')

    // G3 §2.4: route-level gate. Admin routes stay pixel-identical to today
    // even if the user has newUi=true. The `useNewShell` flag is then drilled
    // down so each Chakra element can swap its color/border props to tokens
    // while keeping every other behavior (collapse animation, mobile drawer,
    // scroll containers) intact.
    const isAdminRoute = pathname.startsWith('/admin');
    const profile = useProfile();
    const useNewShell = !isAdminRoute && resolveNewUi(profile.data);

    // G3 §3.5: new shell is light-only this batch. Forcing color mode to
    // 'light' here keeps residual useColorModeValueKey calls inside child
    // components (e.g. the 4 topbar buttons) from flipping to dark values
    // and fighting the new visual. TODO: replace with a true dark token set
    // when the new design system gets a dark mode.
    const {colorMode, setColorMode} = useColorMode();
    useEffect(() => {
        if (useNewShell && colorMode !== 'light') {
            setColorMode('light');
        }
    }, [useNewShell, colorMode, setColorMode]);

    const _oldBg = useColorModeValueKey('bg');
    const contentBg = useNewShell ? 'var(--ink)' : _oldBg;
    return (
        <HStack
            alignItems={'stretch'}
            h={'100vh'}
            w="100vw"
            spacing={0}
            // G3 §2.3 + §2.2: .nx-shell brings the design tokens (background,
            // font, a/button resets) into the layout subtree without firing
            // the full-page `*` reset that .nx carries.
            className={useNewShell ? 'nx-shell' : undefined}
        >
            {isMobile ? (
                <MobileNavDrawer useNewShell={useNewShell} />
            ) : (
                <>
                {/* <DarkMode> */}
                <NavBar useNewShell={useNewShell} />
                {/* </DarkMode> */}
                </>
            )}
            {isUser && <Zendesk />}
            <Stack
                flex={1}
                bg={contentBg}
                transition={'background-color 200ms linear'}
                spacing={0}
                display="flex"
                w="100%"
            >
                <Header useNewShell={useNewShell} />
                <Box overflow={'scroll'} flex={1} zIndex={1} w="100%">
                    <Box
                        px={{base: 4, md: 4}}
                        pt={4}
                        pb={{base: 6, md: 4}}
                        mx="auto"
                        mb={isUser ? 20 : 0}
                        maxW={isUser ? hasIbAgent? '1500px': '1500px' : '100%'}
                        // overflow={'scroll'}
                        // h="100%"
                    >
                        <Outlet />
                    </Box>
                </Box>
            </Stack>
        </HStack>
    );
};

export const StretchAppContnetArea = (props) => {
    return (
        <Stack m={{base: -2, md: -4}} my={-2}>
            {props.children}
        </Stack>
    );
};

export default AppLayout;
